import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from 'antd-mobile';
import { useMemoizedFn } from 'ahooks';
import service from '@/service';
import { selectCurrentServingOrder, setCurrentServingOrder } from '@/store/service';
import { Header, HeaderMemberInfo, ScrollView, ServiceProcessCrumb } from '@/components';
import { ALL_PAGES } from '@/constant';
import { ScrollViewRefAttrs } from '@/components/scroll-view';
import { selectedCurrentCustomer } from '@/store/customer';
import { handleNavFromOrderInfo, Toast } from '@/utils';
import { useNav } from '@/hooks';
import PictureContent, { PictureContentRefAttrs } from './picture-content';

import cx from './index.module.scss';

interface Props {
  className?: string;
}

const ActionTest = ({ className }: Props) => {
  const nav = useNav();
  const dispatch = useDispatch();
  const currentServingOrder = useSelector(selectCurrentServingOrder);
  const [actionTestImgs, setActionTestImgs] = useState([]);
  const currentCustomer = useSelector(selectedCurrentCustomer);
  const scrollViewRef = useRef<ScrollViewRefAttrs>(null);
  const [isHandlingNextStep, setIsHandlingNextStep] = useState<boolean>(false);
  const [questionnaire, setQuestionnaire] = useState<ActionTest.Questionnaire>({} as any);
  const [beforeHandData, setBeforeHandData] = useState([]);
  const [isLoadingQuestionnaire, setIsLoadingQuestionnaire] = useState<boolean>(true);
  const questionnaireIdRef = useRef(null);
  const pictureContentRef = useRef<PictureContentRefAttrs>(null);
  const [isScrollTop, setIsScrollTop] = useState<boolean>(true);

  const onScrolling = useMemoizedFn(pos => {
    setIsScrollTop(pos.y > 0);
  });

  const handleScrollElement = () => {
    const maxScrollY = scrollViewRef.current?.bsInstance?.maxScrollY;
    setIsScrollTop(false);
    scrollViewRef.current?.bsInstance?.scrollTo(0, maxScrollY, 200);
  };

  const getActionTestQuestionnaire = useCallback(
    async serviceOrderId => {
      const { data } = await service.actionTest.getQuestionnaireAndAnswer({
        step: 'ActionTest',
        serviceOrderId,
      });

      questionnaireIdRef.current = data.questionnaireId;

      const parsedQuestionnaire = JSON.parse(data.questionnaire) as ActionTest.Questionnaire;
      setBeforeHandData(data?.beforehand?.map(item => (typeof item === 'string' ? JSON.parse(item) : item || null)));
      setQuestionnaire(parsedQuestionnaire);

      scrollViewRef.current?.handleRefresh();
      setTimeout(() => setIsLoadingQuestionnaire(false), 300);
    },
    [setQuestionnaire, setIsLoadingQuestionnaire]
  );

  const handleNextStep = useCallback(async () => {
    try {
      setIsHandlingNextStep(true);

      const { data: order } = await service.service.getServingOrderByOperator();
      if (!order) {
        Toast.show('检测到长时间未操作，已为您自动结束订单');
        nav(ALL_PAGES.MEMBER_DETAIL);
        return;
      }

      await pictureContentRef.current?.savePainLevel(
        currentServingOrder.orderId,
        questionnaireIdRef.current,
        actionTestImgs?.map(item => JSON.stringify(item))
      );

      // 自定义画线直接调用start接口
      if (order?.stepFlow?.find(item => item.step === 'RoboticTask')?.subStep?.includes('CustomPalpation')) {
        try {
          await service.massage.startRoboticTask({ serviceOrderId: order.orderId });
        } catch (e) {
          Toast.fail(e?.message);
        }
      }

      const { data: orderInfo } = await service.service.getServiceOrder(currentServingOrder.orderId);

      dispatch(setCurrentServingOrder(orderInfo));
      await handleNavFromOrderInfo(orderInfo, nav);
    } catch (e) {
      console.warn(e);
    } finally {
      setIsHandlingNextStep(false);
    }
  }, [actionTestImgs, currentServingOrder.orderId, dispatch, nav]);

  useEffect(() => {
    const { orderId, productItems } = currentServingOrder || {};
    if (!(orderId && productItems?.includes('Shockwave'))) return;
    getActionTestQuestionnaire(orderId);
    pictureContentRef.current?.getPainLevelBefore(orderId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentServingOrder?.orderId]);

  return (
    <div className={cx(className, 'action-test')}>
      <Header
        title={<HeaderMemberInfo name={currentCustomer?.name} avatarUrl={currentCustomer?.avatarUrl} />}
        backConfig={{ text: '会员详情', action: () => nav('/user/member-detail', { replace: true }) }}
      />
      <ServiceProcessCrumb currentPageStep="ActionTest" />
      <ScrollView
        ref={scrollViewRef}
        onScrolling={onScrolling}
        realTime
        className={cx('content-box', { scrolling: !isScrollTop })}
      >
        <div className={cx('content-inner-box')}>
          <PictureContent
            ref={pictureContentRef}
            setActionTestImgs={setActionTestImgs}
            currentServingOrder={currentServingOrder}
            beforeHandData={beforeHandData}
            questionnaire={questionnaire}
            handleScrollElement={handleScrollElement}
          />
        </div>
      </ScrollView>
      <Button
        color="primary"
        className={cx('btn', 'btn-primary', 'next-step', {
          'show-action-point': !(isLoadingQuestionnaire || isHandlingNextStep),
        })}
        loading={isLoadingQuestionnaire || isHandlingNextStep}
        onClick={handleNextStep}
      >
        下一步
      </Button>
    </div>
  );
};

export default ActionTest;
